import React from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getCheckoutStatus } from "../lib/api";
import { trackPurchase } from "../lib/analytics";

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL = 2000;

export default function Success() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  const [status, setStatus] = React.useState("checking"); // checking | paid | pending | failed | expired | error
  const [order, setOrder] = React.useState(null);
  const [attempts, setAttempts] = React.useState(0);

  React.useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let active = true;
    let tries = 0;

    const poll = async () => {
      if (!active) return;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (!active) return;
        setOrder(data?.order || null);
        if (data?.payment_status === "paid") {
          setStatus("paid");
          const o = data?.order;
          if (o?.order_id) {
            trackPurchase({
              transactionId: o.order_id,
              amount: o.amount,
              currency: o.currency,
              styleId: o.style_id,
              styleName: o.style_name,
            });
          }
          return;
        }
        if (data?.status === "expired") {
          setStatus("expired");
          return;
        }
        tries += 1;
        setAttempts(tries);
        if (tries >= MAX_ATTEMPTS) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (!active) return;
        setStatus("error");
      }
    };

    setStatus("checking");
    poll();

    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-32 pb-20 bg-radial-cream">
        <div className="max-w-2xl mx-auto px-6 text-center">
          {status === "checking" && (
            <div>
              <Loader2 className="h-12 w-12 text-[#b59557] mx-auto animate-spin" />
              <h1 className="mt-6 font-serif text-3xl md:text-4xl text-[#1a1816]">
                Comprobando tu <span className="italic text-[#b59557]">pago</span>...
              </h1>
              <p className="mt-3 text-[#4a4136]">
                Un momento por favor, estamos confirmando con Stripe.
              </p>
              <p className="mt-2 text-xs text-[#8a7340]">Intento {attempts}/{MAX_ATTEMPTS}</p>
            </div>
          )}

          {status === "paid" && (
            <div>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#efe6cf] border border-[#e2d5b3] flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9 text-[#8a7340]" />
              </div>
              <h1 className="mt-6 font-serif text-3xl md:text-4xl text-[#1a1816]">
                ¡Pago <span className="italic text-[#b59557]">confirmado</span>!
              </h1>
              <p className="mt-3 text-[#4a4136]">
                Gracias {order?.customer_name || ""}. Hemos recibido tu pedido y contactaremos contigo en breve al correo
                {" "}<span className="text-[#1a1816]">{order?.email}</span>.
              </p>

              {order && (
                <div className="mt-8 rounded-2xl border border-[#e8ddc3] bg-white p-6 text-left">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[#b59557] mb-2">
                    Resumen del pedido
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-serif text-xl text-[#1a1816]">
                        Retrato {order.style_name}
                      </div>
                      {order.include_pet_name && order.pet_name && (
                        <div className="mt-1 text-sm text-[#4a4136]">
                          Con el nombre: <span className="italic text-[#8a7340]">{order.pet_name}</span>
                        </div>
                      )}
                      <div className="mt-1 text-xs text-[#7a6a48]">
                        20×15×3 cm · Envío incluido
                      </div>
                    </div>
                    <div className="font-serif italic text-2xl text-[#8a7340]">
                      {Number(order.amount).toFixed(0)}€
                    </div>
                  </div>
                </div>
              )}

              <Link
                to="/"
                className="mt-8 inline-flex items-center gap-2 btn-primary rounded-full px-6 py-3 text-sm font-medium"
              >
                <ArrowLeft className="h-4 w-4" /> Volver al inicio
              </Link>
            </div>
          )}

          {(status === "pending" || status === "expired" || status === "error" || status === "failed") && (
            <div>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#f0e3e0] border border-[#e8cbc4] flex items-center justify-center">
                <XCircle className="h-9 w-9 text-[#a84b3a]" />
              </div>
              <h1 className="mt-6 font-serif text-3xl md:text-4xl text-[#1a1816]">
                {status === "pending" && "Pago en proceso"}
                {status === "expired" && "Sesión expirada"}
                {(status === "error" || status === "failed") && "No pudimos confirmar el pago"}
              </h1>
              <p className="mt-3 text-[#4a4136] max-w-md mx-auto">
                {status === "pending"
                  ? "Tu pago aún se está procesando. Recibirás un correo en cuanto se complete."
                  : "Puedes volver a intentarlo desde el inicio cuando quieras."}
              </p>
              <Link
                to="/"
                className="mt-8 inline-flex items-center gap-2 btn-primary rounded-full px-6 py-3 text-sm font-medium"
              >
                <ArrowLeft className="h-4 w-4" /> Volver al inicio
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
